import { getApiErrorMessage } from "../../utils/functions/api-errors";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import { RadioButton } from "primereact/radiobutton";
import { FormField, FormRow, FormSection, FormToggle } from "../ui";
import { useIsFeatureFlagEnabled } from "../../hooks/useIsFeatureFlagEnabled";
import {
  ATTENDANCE_DEFAULT_MAX_ATTENDEES,
  ATTENDANCE_DEFAULT_WINDOW_END_HOURS,
  ATTENDANCE_DEFAULT_WINDOW_START_OFFSET_MIN,
  AttendanceConfigDraft,
  AttendanceMethod,
  getAttendanceConfigErrors,
} from "../../models/attendance";
import { useGetEventAttendanceConfigQuery, useGetAttendanceHostOptionsQuery } from "../../store/services/attendances";

interface AttendanceConfigSectionProps {
  tenantId: string | null;
  eventId: number | null;
  onDraftChange: (draft: AttendanceConfigDraft | null) => void;
  number?: number;
  defaultOpen?: boolean;
  disabled?: boolean;
  onReadyChange?: (ready: boolean) => void;
  validationAttempt?: number;
}

interface AttendanceMethodOption {
  label: string;
  id: string;
  value: AttendanceMethod | "QR" | "MANUAL_QR";
  disabled: boolean;
}

const METHOD_OPTIONS: AttendanceMethodOption[] = [
  { label: "Manual desde APP", id: "manual-app", value: "MANUAL_APP", disabled: false },
  { label: "QR", id: "qr", value: "QR", disabled: true },
  { label: "Manual + QR", id: "manual-qr", value: "MANUAL_QR", disabled: true },
];

const COMING_SOON_BADGE_STYLE = {
  backgroundColor: "#CCCCFF",
  color: "var(--text-color)",
  borderRadius: "var(--radius-md)",
  padding: "2px 10px",
  fontSize: "var(--font-small-size)",
  fontWeight: 600,
  lineHeight: "var(--font-small-line)",
  whiteSpace: "nowrap",
} as const;

const DEFAULT_DRAFT: AttendanceConfigDraft = {
  configId: null,
  enabled: false,
  method: "MANUAL_APP",
  attendanceHostUserId: null,
  maxAttendees: ATTENDANCE_DEFAULT_MAX_ATTENDEES,
  windowStartOffsetMin: ATTENDANCE_DEFAULT_WINDOW_START_OFFSET_MIN,
  windowEndHours: ATTENDANCE_DEFAULT_WINDOW_END_HOURS,
};

const DEFAULT_HOST_OPTION = { label: "Mismo host del evento", value: null };

export const AttendanceConfigSection = ({
  tenantId,
  eventId,
  onDraftChange,
  number,
  defaultOpen,
  disabled = false,
  onReadyChange,
  validationAttempt,
}: AttendanceConfigSectionProps) => {
  const { enabled: isFlagEnabled, loading: isLoadingFlag } = useIsFeatureFlagEnabled(
    "attendance_manual_enabled",
    tenantId
  );
  const [draft, setDraft] = useState<AttendanceConfigDraft>(DEFAULT_DRAFT);
  const initializedConfigIdRef = useRef<number | null>(null);

  const shouldLoadEventData = !isLoadingFlag && isFlagEnabled && eventId !== null;

  const {
    currentData: config,
    isError: isConfigError,
    error: configError,
    refetch: refetchConfig,
    isFetching: isFetchingConfig,
  } = useGetEventAttendanceConfigQuery(eventId ?? 0, {
    skip: !shouldLoadEventData,
  });
  const { currentData: hosts } = useGetAttendanceHostOptionsQuery(eventId ?? 0, {
    skip: !shouldLoadEventData,
  });

  const waitingForConfig = shouldLoadEventData && config === undefined && !isConfigError;
  const draftMatchesLoadedConfig =
    config === undefined || config === null || draft.configId === config.id;
  const isReady = !isLoadingFlag && !waitingForConfig && draftMatchesLoadedConfig;
  const canEditConfig = isReady && !(shouldLoadEventData && config === undefined);

  useEffect(() => {
    onReadyChange?.(isReady);
  }, [isReady, onReadyChange]);

  useEffect(() => {
    if (!config) return;
    if (initializedConfigIdRef.current === config.id) return;

    initializedConfigIdRef.current = config.id;
    setDraft({
      configId: config.id,
      enabled: config.enabled,
      method: config.method,
      attendanceHostUserId: config.attendanceHostUserId,
      maxAttendees: config.maxAttendees,
      windowStartOffsetMin: config.windowStartOffsetMin,
      windowEndHours: config.windowEndHours,
    });
  }, [config]);

  useEffect(() => {
    if (!isReady) return;
    if (!isFlagEnabled || (shouldLoadEventData && config === undefined)) {
      onDraftChange(null);
      return;
    }
    onDraftChange(draft);
  }, [config, draft, isFlagEnabled, isReady, onDraftChange, shouldLoadEventData]);

  const hostOptions = useMemo(() => [
    DEFAULT_HOST_OPTION,
    ...(hosts ?? []).map((host) => ({ label: host.name, value: host.id })),
  ], [hosts]);
  const validationErrors = getAttendanceConfigErrors(draft);

  const updateDraft = (changes: Partial<AttendanceConfigDraft>) => {
    setDraft((current) => ({ ...current, ...changes }));
  };

  const changeMaxAttendees = (event: { value?: number | null }) => updateDraft({
    maxAttendees: event.value ?? ATTENDANCE_DEFAULT_MAX_ATTENDEES,
  });
  const changeWindowStart = (event: { value?: number | null }) => updateDraft({
    windowStartOffsetMin: event.value ?? ATTENDANCE_DEFAULT_WINDOW_START_OFFSET_MIN,
  });
  const changeWindowEnd = (event: { value?: number | null }) => updateDraft({
    windowEndHours: event.value ?? ATTENDANCE_DEFAULT_WINDOW_END_HOURS,
  });

  if (isLoadingFlag || !isFlagEnabled) {
    return null;
  }

  return (
    <FormSection
      title="Asistencia"
      number={number}
      defaultOpen={defaultOpen}
      validationAttempt={validationAttempt}
    >
      {isConfigError && (
        <div className="mb-3">
          <Message severity="error" text={getApiErrorMessage(configError)} />
          <Button
            type="button"
            label="Reintentar"
            icon="pi pi-replay"
            outlined
            size="small"
            className="mt-2"
            loading={isFetchingConfig}
            onClick={() => {
              void refetchConfig();
            }}
          />
        </div>
      )}
      <fieldset disabled={disabled || !canEditConfig} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <FormToggle
        label="Tomar asistencia en este evento"
        htmlFor="attendanceEnabled"
        hint="El host puede registrar la asistencia manual desde la app; el BO la ve, la corrige y la exporta."
      >
        <InputSwitch
          inputId="attendanceEnabled"
          disabled={disabled || !canEditConfig}
          checked={draft.enabled}
          onChange={(event) => updateDraft({ enabled: event.value })}
        />
      </FormToggle>

      {draft.enabled && (
        <>
          <FormRow>
            <FormField label="Método de registro">
              <div className="flex flex-column" style={{ gap: "var(--space-16)" }}>
                {METHOD_OPTIONS.map((option) => (
                  <div key={option.id} className="flex align-items-center gap-2">
                    <RadioButton
                      inputId={`attendanceMethod-${option.id}`}
                      name="attendanceMethod"
                      value={option.value}
                      checked={option.value === draft.method}
                      disabled={option.disabled || disabled || !canEditConfig}
                      onChange={() => {
                        if (!option.disabled && option.value === "MANUAL_APP") {
                          updateDraft({ method: option.value });
                        }
                      }}
                    />
                    <label
                      htmlFor={`attendanceMethod-${option.id}`}
                      className={option.disabled ? "text-500" : "cursor-pointer"}
                    >
                      {option.label}
                    </label>
                    {option.disabled && (
                      <span style={COMING_SOON_BADGE_STYLE}>Próximamente</span>
                    )}
                  </div>
                ))}
              </div>
            </FormField>

            <FormField label="Quién toma asistencia" htmlFor="attendanceHost">
              <Dropdown
                inputId="attendanceHost"
                value={draft.attendanceHostUserId}
                options={hostOptions}
                optionLabel="label"
                optionValue="value"
                placeholder={DEFAULT_HOST_OPTION.label}
                filter
                disabled={!hosts?.length || disabled || !canEditConfig}
                showClear
                onChange={(event) => updateDraft({ attendanceHostUserId: event.value ?? null })}
                className="w-full"
              />
            </FormField>
          </FormRow>

          {(eventId === null || hosts?.length === 0) && (
            <div className="mb-4">
              <Message
                severity="info"
                className="w-full justify-content-start"
                text="Primero asigná un host al evento"
              />
            </div>
          )}

          <FormRow>
            <FormField
              label="Límite máximo de inscriptos para asistencia"
              htmlFor="attendanceMaxAttendees"
              error={validationErrors.maxAttendees}
              required
              hint="Si el evento supera este número de inscriptos, la toma de asistencia manual se deshabilita."
            >
              <InputNumber
                inputId="attendanceMaxAttendees"
                value={draft.maxAttendees}
                disabled={disabled || !canEditConfig}
                showButtons
                useGrouping={false}
                onChange={changeMaxAttendees}
                onValueChange={changeMaxAttendees}
                invalid={Boolean(validationErrors.maxAttendees)}
              />
            </FormField>

            <FormField
              label="Minutos antes del inicio"
              htmlFor="attendanceWindowStart"
              hint="Entre 0 y 120 minutos antes del inicio (máximo 2 horas)."
              error={validationErrors.windowStartOffsetMin}
            >
              <InputNumber
                inputId="attendanceWindowStart"
                value={draft.windowStartOffsetMin}
                disabled={disabled || !canEditConfig}
                showButtons
                useGrouping={false}
                onChange={changeWindowStart}
                onValueChange={changeWindowStart}
                invalid={Boolean(validationErrors.windowStartOffsetMin)}
              />
            </FormField>

            <FormField
              label="Horas después del inicio"
              htmlFor="attendanceWindowEnd"
              hint="Entre 1 y 168 horas después del inicio."
              error={validationErrors.windowEndHours}
            >
              <InputNumber
                inputId="attendanceWindowEnd"
                value={draft.windowEndHours}
                disabled={disabled || !canEditConfig}
                showButtons
                useGrouping={false}
                onChange={changeWindowEnd}
                onValueChange={changeWindowEnd}
                invalid={Boolean(validationErrors.windowEndHours)}
              />
            </FormField>
          </FormRow>
        </>
      )}
      </fieldset>
    </FormSection>
  );
};
